class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._keyupHandler = (evt) => evt.code === 'Escape' && this.close();
    // Delegated from `this` so the listener survives DOM replacement inside renderContents().
    // Direct binding to #CartDrawer-Overlay was re-added on every renderContents() call, stacking handlers.
    // Uses closest() to handle event bubbling: a child element inside the overlay would
    // fail an === id check, but closest() traverses up the composed path correctly.
    this._overlayClickHandler = (evt) => evt.target.closest('#CartDrawer-Overlay') && this.close();
    this._trapFocusHandler = this._onOpenTransitionEnd.bind(this);
  }

  connectedCallback() {
    this.addEventListener('keyup', this._keyupHandler);
    this.addEventListener('click', this._overlayClickHandler);
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    this.removeEventListener('keyup', this._keyupHandler);
    this.removeEventListener('click', this._overlayClickHandler);
    this._teardownExternalListeners();
  }

  // LOW-01: Removes the cartLink click/keydown listeners that live on an EXTERNAL node
  // (#cart-icon-bubble, in the header section). Those listeners persist across
  // cart-drawer section re-renders because the header is a separate Shopify section.
  // Without this teardown, after a Theme Editor re-render:
  //   • Old listeners survive on cartLink, pointing to the detached CartDrawer instance.
  //   • New instance's connectedCallback() finds dataset.cartLinked='true' and early-returns.
  //   • Result: cart icon triggers the detached instance's open() - cart never opens.
  // Also deletes the dataset guard so the fresh instance can re-register correctly.
  _teardownExternalListeners() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || !cartLink.dataset.cartLinked) return;
    cartLink.removeEventListener('click', this._cartLinkClickHandler);
    cartLink.removeEventListener('keydown', this._cartLinkKeydownHandler);
    delete cartLink.dataset.cartLinked;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    // Dataset guard: cartLink lives in the header and persists across cart-drawer
    // section re-renders in the Theme Editor. Without this guard, each connectedCallback()
    // call would stack a new click + keydown handler on the same persistent node.
    // _teardownExternalListeners() deletes this flag on disconnect so the fresh
    // instance can re-register correctly after a section re-render.
    if (!cartLink || cartLink.dataset.cartLinked) return;
    cartLink.dataset.cartLinked = 'true';

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    // LOW-01: Store handlers as instance properties so _teardownExternalListeners()
    // can pass the exact same function reference to removeEventListener().
    // Anonymous closures cannot be removed by reference - this was the root leak.
    this._cartLinkClickHandler = (event) => {
      event.preventDefault();
      this.open(cartLink);
    };
    this._cartLinkKeydownHandler = (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    };

    cartLink.addEventListener('click', this._cartLinkClickHandler);
    cartLink.addEventListener('keydown', this._cartLinkKeydownHandler);
  }

  _onOpenTransitionEnd() {
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    // MED-01: null guard - either selector returning null would throw in trapFocus's querySelectorAll.
    if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    // MED-02: remove before add - ensures exactly one handler regardless of how rapidly open() is called.
    // removeEventListener matches by (type, callback, capture); the { once } flag is not part of identity.
    this.removeEventListener('transitionend', this._trapFocusHandler);
    this.addEventListener('transitionend', this._trapFocusHandler, { once: true });

    document.body.classList.add('overflow-hidden');

    // BFCache zombie fix: iOS/Android back-navigation restores a frozen snapshot.
    // If the cart was open during navigation, force-close it on restore.
    // Instance flag prevents duplicate listeners from renderContents() re-calling open().
    if (!this._pageshowGuard) {
      this._pageshowGuard = (e) => {
        if (e.persisted) this.close();
        this._pageshowGuard = null;
      };
      window.addEventListener('pageshow', this._pageshowGuard, { once: true });
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    // Clean up BFCache guard on normal close so it doesn't fire on the next pageshow.
    if (this._pageshowGuard) {
      window.removeEventListener('pageshow', this._pageshowGuard);
      this._pageshowGuard = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    // Null-safe: mirrors the same pattern in CartItems and CartNotification.
    // Defensive against undefined html (e.g. unexpected API response format).
    if (!html) return '';
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const el = parsed.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// LOW-01: Belt-and-suspenders for Shopify Theme Editor DOM morphing scenarios.
// disconnectedCallback() is reliable for standard section removal, but the Theme Editor
// can occasionally morph sections without triggering a full custom element lifecycle.
// This guard ensures _teardownExternalListeners() fires on every section destruction,
// preventing the dead-instance cartLink handler from surviving a virtual DOM update.
// Pattern mirrors cinematic-slider.js lines 223-229.
document.addEventListener('shopify:section:unload', (event) => {
  const cartDrawer = event.target.querySelector('cart-drawer');
  if (cartDrawer) cartDrawer._teardownExternalListeners();
});

// RD-05: When the HEADER section reloads in Theme Editor, #cart-icon-bubble is a fresh node.
// CartDrawer stays connected (it's in a separate section) so connectedCallback() does not re-fire,
// meaning setHeaderCartIconAccessibility() is never called on the fresh node - cart icon goes dead.
// The old node's handlers were GC'd with the old node; no teardown is needed before re-registering.
// After re-registration, this._cartLinkClickHandler/Keydown reference the fresh closures, so
// _teardownExternalListeners() will correctly remove them on the next CartDrawer disconnect.
document.addEventListener('shopify:section:load', (event) => {
  if (!event.target.querySelector('#cart-icon-bubble')) return;
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) cartDrawer.setHeaderCartIconAccessibility();
});

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
